import { useTranslation } from 'react-i18next';
import { PickupData, ConfigDataObj } from '../../../types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Input, Modal, Spin, Switch } from 'antd';
import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../../utils/axiosRequest';
import { alertMessage } from '../../../utils/alertMessage';
import { getFormField } from '../../../utils/getFormField';

type EditPickupModalProps = {
  pickupData?: PickupData;
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  refresh?: () => void;
};

const EditPickupModal = ({
  pickupData,
  visible,
  setVisible,
  refresh,
}: EditPickupModalProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const isSubscribed = useRef(true);

  const [tplConfigData, setTplConfigData] = useState<ConfigDataObj[]>([]);

  // Sets isSubscribed to false if component becomes unmounted
  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  /**
   * makes a request and grabs the tpl config info and loads it
   */
  const getTPLConfigData = useCallback(
    (shippingCode?: string) => {
      if (isSubscribed.current && shippingCode) setIsLoading(true);
      shippingCode &&
        getDataWithAuthToken('shipping/transport/tpl/config', {
          params: { shippingCode: shippingCode },
        })
          .then((response) => {
            if (response) {
              if (response.goodStatus) {
                if (isSubscribed.current) {
                  setTplConfigData(response.data.list);
                }
              } else {
                alertMessage(
                  'error',
                  response?.msg || t('general.noResponse'),
                  response?.data || undefined
                );
              }
            }
            if (isSubscribed.current) setIsLoading(false);
          })
          .catch((err: any) => {
            if (isSubscribed.current) setIsLoading(true);
            console.log(err);
          });
    },
    [t]
  );

  // Things to do on first render
  useEffect(() => {
    if (isSubscribed.current && visible) {
      form.resetFields();
      getTPLConfigData(pickupData?.shippingCode);
    }
  }, [form, visible, pickupData, getTPLConfigData]);

  const onFinish = () => {
    form
      .validateFields()
      .then((values: any) => {
        if (isLoading) {
          setIsLoading(true);
        }
        let configData: any[] = [];
        Object.keys(values).forEach((key) => {
          values[key] &&
            key !== 'isEnabled' &&
            key !== 'pickUpAreaName' &&
            configData.push({
              name: key,
              value: String(
                typeof values[key] === 'boolean'
                  ? values[key]
                    ? 1
                    : 0
                  : values[key]
              ),
            });
        });

        postDataWithAuthToken('pick_up/edit', {
          pickUpAreaId: pickupData?.pickUpAreaId,
          pickUpAreaName: form.getFieldValue('pickUpAreaName'),
          isEnabled: form.getFieldValue('isEnabled'),
          configure: configData,
        }).then((response) => {
          if (response && response.goodStatus) {
            alertMessage('success', t('settings.alerts.pickupAreaEdited'));
            onClose();
          } else {
            alertMessage(
              'error',
              response?.msg || t('general.noResponse'),
              response?.data || undefined
            );
          }
          if (isLoading) {
            setIsLoading(false);
          }
        });
      })
      .catch((err: any) => {
        if (isLoading) {
          setIsLoading(false);
        }
        console.log(err);
      });
  };

  const onClose = () => {
    setIsLoading(false);
    setVisible(false);
    refresh && refresh();
  };

  return (
    <Modal
      getContainer={false}
      maskClosable={false}
      onOk={onFinish}
      onCancel={onClose}
      width={550}
      bodyStyle={{ height: 'auto' }}
      style={{ height: 500 }}
      title={`${t('settings.add/editPickup.editTitle')}`}
      visible={visible}
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          initialValues={{
            pickUpAreaName: pickupData?.pickUpAreaName,
            isEnabled: pickupData?.isEnabled,
          }}
        >
          <Form.Item
            name="pickUpAreaName"
            label={t('settings.pickupListColumns.pickUpAreaName')}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="isEnabled"
            valuePropName="checked"
            label={t('settings.pickupListColumns.isEnabled')}
          >
            <Switch />
          </Form.Item>
          {tplConfigData &&
            tplConfigData.map((config) => {
              return getFormField(config, [], form, {
                currentValues: pickupData?.configure,
              });
            })}
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditPickupModal;
